import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useParticipants,
  useLocalParticipant,
  useRoomContext,
  VideoTrack,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  getMeetingJoinTokenApi,
  joinMeetingApi,
  leaveMeetingApi,
  startMeetingRecordingApi,
  stopRecordingApi,
  kickParticipantApi,
  muteParticipantApi,
  unmuteParticipantApi,
  disableParticipantVideoApi,
  enableParticipantVideoApi,
  checkWaitingRoomStatusApi,
  requestToJoinWaitingRoomApi,
  getPendingWaitingRoomRequestsApi,
  approveWaitingRoomParticipantApi,
  rejectWaitingRoomParticipantApi,
} from "../../../../api/api";
import { getInitials, getAvatarColor } from "./avatarUtils";
import "@livekit/components-styles";
import "./MeetingPage.scss";

// ==================== ICONS ====================
const MicIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const MicOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
    <path d="M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
    <path d="M12 19v4" />
  </svg>
);

const CameraIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const CameraOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3" />
    <path d="M23 7l-7 5 7 5V7z" />
  </svg>
);

const ScreenShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const LeaveIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const ParticipantsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RecordIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
  >
    <circle cx="12" cy="12" r="8" />
  </svg>
);

const StopIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
  >
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const KickIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const WaitingRoomIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 9h6" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </svg>
);

// ==================== PARTICIPANT TILE ====================
const ParticipantTile = ({ participant, isLocal, isSpeaking, isHost }) => {
  const cameraTracks = useTracks([Track.Source.Camera]);
  const videoTrack = cameraTracks.find(
    (t) => t.participant?.identity === participant?.identity,
  );

  const displayName = participant?.name || "Anonymous";
  const initials = getInitials(displayName);
  const avatarColor = getAvatarColor(participant?.identity || displayName);
  const isCameraEnabled = participant?.isCameraEnabled !== false;
  const hasVideo = !!videoTrack?.publication && isCameraEnabled;
  const micEnabled = participant?.isMicrophoneEnabled !== false;

  return (
    <div
      className={`mursal-participant-tile ${isSpeaking ? "mursal-participant-tile--speaking" : ""}`}
    >
      <div className="mursal-participant-tile__center">
        {hasVideo ? (
          <VideoTrack
            trackRef={videoTrack}
            className="mursal-participant-tile__video"
            muted={isLocal}
          />
        ) : (
          <div
            className="mursal-participant-tile__avatar"
            style={{ backgroundColor: avatarColor }}
          >
            {initials}
          </div>
        )}
      </div>
      {!micEnabled && (
        <div className="mursal-participant-tile__mic-badge">
          <MicOffIcon />
        </div>
      )}
      <div className="mursal-participant-tile__overlay">
        <div className="mursal-participant-tile__info">
          <span className="mursal-participant-tile__name">
            {displayName}{" "}
            {isLocal && (
              <span className="mursal-participant-tile__you-tag">You</span>
            )}
          </span>
          {isHost && (
            <span className="mursal-participant-tile__host-badge">Host</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== SCREEN SHARE VIEW ====================
const ScreenShareView = () => {
  const screenTracks = useTracks([Track.Source.ScreenShare]);
  const activeTrack = screenTracks.find((t) => t.publication);
  return (
    <div className="mursal-screen-share">
      {activeTrack ? (
        <VideoTrack
          trackRef={activeTrack}
          className="mursal-screen-share__video"
        />
      ) : (
        <div className="mursal-screen-share__placeholder">
          <ScreenShareIcon />
          <span>Waiting for screen share…</span>
        </div>
      )}
    </div>
  );
};

// ==================== WAITING ROOM COMPONENT ====================
const WaitingRoom = ({ meetingId, meetingName, onApproved }) => {
  const [status, setStatus] = useState("CHECKING");
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    checkStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const checkStatus = async () => {
    try {
      setLoading(true);
      const res = await checkWaitingRoomStatusApi(meetingId);
      if (res.data?.success) {
        if (res.data.allowed) {
          setStatus("APPROVED");
          if (pollRef.current) clearInterval(pollRef.current);
          setTimeout(() => onApproved(), 500);
        } else {
          setStatus(res.data.status || "NOT_REQUESTED");
          if (res.data.status === "PENDING" && !pollRef.current) {
            pollRef.current = setInterval(checkStatus, 5000);
          }
        }
      }
    } catch (err) {
      setStatus("NOT_REQUESTED");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToJoin = async () => {
    try {
      setLoading(true);
      const res = await requestToJoinWaitingRoomApi(meetingId);
      if (res.data?.success) {
        setStatus("PENDING");
        pollRef.current = setInterval(checkStatus, 5000);
      }
    } catch (err) {
      console.error("Failed to request to join:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mursal-prejoin">
        <div className="mursal-prejoin__card">
          <h2 className="mursal-prejoin__title">{meetingName}</h2>
          <p className="mursal-prejoin__subtitle">Checking access...</p>
          <div
            className="mursal-loading__spinner"
            style={{ margin: "0 auto" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mursal-prejoin">
      <div className="mursal-prejoin__card">
        <h2 className="mursal-prejoin__title">{meetingName}</h2>
        {status === "NOT_REQUESTED" && (
          <>
            <p className="mursal-prejoin__subtitle">
              This meeting has a waiting room enabled. You need to request
              permission to join.
            </p>
            <button
              type="button"
              className="mursal-prejoin__join-btn"
              onClick={handleRequestToJoin}
            >
              Ask to Join
            </button>
          </>
        )}
        {status === "PENDING" && (
          <>
            <p className="mursal-prejoin__subtitle">
              ⏳ Your request has been sent to the host. Waiting for approval...
            </p>
            <div
              className="mursal-loading__spinner"
              style={{ margin: "0 auto" }}
            />
            <p
              style={{ color: "#9ca3af", fontSize: "12px", marginTop: "12px" }}
            >
              You will be automatically connected when approved.
            </p>
          </>
        )}
        {status === "REJECTED" && (
          <>
            <p
              className="mursal-prejoin__subtitle"
              style={{ color: "#ef4444" }}
            >
              ❌ Your request to join was rejected by the host.
            </p>
            <button
              type="button"
              className="mursal-prejoin__join-btn"
              onClick={handleRequestToJoin}
            >
              Ask Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== HOST WAITING ROOM PANEL ====================
const HostWaitingRoomPanel = ({ meetingId, onClose }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 5000);
    return () => clearInterval(interval);
  }, [meetingId]);

  const fetchRequests = async () => {
    try {
      const res = await getPendingWaitingRoomRequestsApi(meetingId);
      if (res.data?.success) setRequests(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch waiting room requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveWaitingRoomParticipantApi(meetingId, userId);
      fetchRequests();
    } catch (err) {
      console.error("Failed to approve:", err);
    }
  };

  const handleReject = async (userId) => {
    try {
      await rejectWaitingRoomParticipantApi(meetingId, userId);
      fetchRequests();
    } catch (err) {
      console.error("Failed to reject:", err);
    }
  };

  return (
    <div className="mursal-meeting__participants-panel">
      <div className="mursal-meeting__participants-header">
        <h6>Waiting Room ({requests.length})</h6>
        <button
          className="mursal-meeting__participants-close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div className="mursal-meeting__participants-list">
        {loading ? (
          <p style={{ color: "#9ca3af", textAlign: "center", padding: "20px" }}>
            Loading...
          </p>
        ) : requests.length === 0 ? (
          <p style={{ color: "#9ca3af", textAlign: "center", padding: "20px" }}>
            No pending requests
          </p>
        ) : (
          requests.map((req) => (
            <div key={req.id} className="mursal-participant-item">
              <div
                className="mursal-participant-item__avatar"
                style={{ backgroundColor: "#5b93ff" }}
              >
                {getInitials(req.userId?.substring(0, 2) || "?")}
                <span className="mursal-participant-item__status-dot" />
              </div>
              <div className="mursal-participant-item__info">
                <div className="mursal-participant-item__info-top">
                  <span className="mursal-participant-item__info-name">
                    {req.userId?.substring(0, 8)}...
                  </span>
                </div>
                <div className="mursal-participant-item__info-email">
                  Requested {new Date(req.requestedAt).toLocaleTimeString()}
                </div>
              </div>
              <div className="mursal-participant-item__actions">
                <button
                  className="mursal-participant-item__action-btn"
                  onClick={() => handleApprove(req.userId)}
                  title="Approve"
                  style={{ color: "#1c9a57" }}
                >
                  ✓
                </button>
                <button
                  className="mursal-participant-item__action-btn mursal-participant-item__action-btn--danger"
                  onClick={() => handleReject(req.userId)}
                  title="Reject"
                >
                  ✕
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ==================== PRE-JOIN LOBBY (FIXED) ====================
const PreJoinLobby = ({ meetingName, onJoin }) => {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [previewStream, setPreviewStream] = useState(null);
  const [deviceError, setDeviceError] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    // ✅ SAFETY CHECK: Ensure navigator.mediaDevices exists
    if (!navigator?.mediaDevices?.getUserMedia) {
      setDeviceError(
        "Camera/microphone access is not available. Please use HTTPS or localhost.",
      );
      setPreviewStream(null);
      return;
    }

    let stream;
    let cancelled = false;
    if (!audioEnabled && !videoEnabled) {
      setPreviewStream(null);
      setDeviceError(null);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ audio: audioEnabled, video: videoEnabled })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        setPreviewStream(s);
        setDeviceError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewStream(null);
          setDeviceError(
            "Couldn't access camera/microphone. " + (err.message || ""),
          );
        }
      });
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    if (videoRef.current)
      videoRef.current.srcObject = videoEnabled ? previewStream : null;
  }, [previewStream, videoEnabled]);

  const handleJoin = () => {
    if (previewStream) previewStream.getTracks().forEach((t) => t.stop());
    onJoin({ audioEnabled, videoEnabled });
  };

  return (
    <div className="mursal-prejoin">
      <div className="mursal-prejoin__card">
        <h2 className="mursal-prejoin__title">{meetingName}</h2>
        <p className="mursal-prejoin__subtitle">
          Check your camera and microphone before joining
        </p>
        <div className="mursal-prejoin__preview">
          {videoEnabled && previewStream ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="mursal-prejoin__preview-video"
            />
          ) : (
            <div className="mursal-prejoin__preview-placeholder">
              <CameraOffIcon />
              <span>Camera is off</span>
            </div>
          )}
        </div>
        {deviceError && (
          <div className="mursal-prejoin__error">{deviceError}</div>
        )}
        <div className="mursal-prejoin__toggles">
          <button
            type="button"
            className={`mursal-prejoin__toggle-btn ${audioEnabled ? "mursal-prejoin__toggle-btn--active" : ""}`}
            onClick={() => setAudioEnabled((v) => !v)}
          >
            {audioEnabled ? <MicIcon /> : <MicOffIcon />}
            <span>{audioEnabled ? "Mic on" : "Mic off"}</span>
          </button>
          <button
            type="button"
            className={`mursal-prejoin__toggle-btn ${videoEnabled ? "mursal-prejoin__toggle-btn--active" : ""}`}
            onClick={() => setVideoEnabled((v) => !v)}
          >
            {videoEnabled ? <CameraIcon /> : <CameraOffIcon />}
            <span>{videoEnabled ? "Camera on" : "Camera off"}</span>
          </button>
        </div>
        <button
          type="button"
          className="mursal-prejoin__join-btn"
          onClick={handleJoin}
        >
          Join now
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN MEETING COMPONENT ====================
const RealMeeting = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [connection, setConnection] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingName, setMeetingName] = useState("Meeting");
  const [hostId, setHostId] = useState(null);
  const [joinChoices, setJoinChoices] = useState(null);
  const [hasJoined, setHasJoined] = useState(false);
  const [waitingRoomChecked, setWaitingRoomChecked] = useState(false);
  const [inWaitingRoom, setInWaitingRoom] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);
        const response = await getMeetingJoinTokenApi(meetingId);
        if (response.data?.success) {
          setConnection({
            url: response.data.livekitUrl,
            token: response.data.token,
          });
          setMeetingName(response.data.meetingName || "Meeting");
          setHostId(response.data.hostId || null);
        } else {
          setError("Failed to get join token");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to join meeting");
      } finally {
        setLoading(false);
      }
    };
    fetchToken();
  }, [meetingId]);

  useEffect(() => {
    if (joinChoices && !waitingRoomChecked) {
      checkWaitingRoomStatusApi(meetingId)
        .then((res) => {
          setWaitingRoomChecked(true);
          if (res.data?.success) {
            if (res.data.allowed) {
              setIsApproved(true);
            } else {
              setInWaitingRoom(true);
            }
          }
        })
        .catch(() => {
          setWaitingRoomChecked(true);
          setIsApproved(true);
        });
    }
  }, [joinChoices, meetingId, waitingRoomChecked]);

  useEffect(() => {
    if (isApproved && !hasJoined && waitingRoomChecked) {
      joinMeetingApi(meetingId)
        .then(() => setHasJoined(true))
        .catch((err) => console.error("Failed to call join meeting API:", err));
    }
  }, [isApproved, hasJoined, meetingId, waitingRoomChecked]);

  const handleApproved = () => {
    setInWaitingRoom(false);
    setIsApproved(true);
  };

  const handleLeave = async () => {
    try {
      await leaveMeetingApi(meetingId);
    } catch (e) {}
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const role = authUser?.role;
    if (
      [
        "ADMIN",
        "SUPER_ADMIN",
        "OPERATION_ADMIN",
        "SUPPORT_ADMIN",
        "GROUP_ADMIN",
        "AUDITOR",
        "VIEWER",
      ].includes(role)
    ) {
      navigate("/meetings/default");
    } else {
      localStorage.clear();
      navigate("/login");
    }
  };

  if (loading)
    return (
      <div className="mursal-loading">
        <div className="mursal-loading__spinner" />
      </div>
    );
  if (error)
    return (
      <div className="mursal-error-screen">
        <div className="mursal-error-screen__icon">
          <AlertIcon />
        </div>
        <p className="mursal-error-screen__message">{error}</p>
        <button className="mursal-error-screen__action" onClick={handleLeave}>
          Back to Meetings
        </button>
      </div>
    );
  if (!connection || !connection.url) return null;

  if (!joinChoices) {
    return (
      <div className="mursal-meeting-page">
        <PreJoinLobby meetingName={meetingName} onJoin={setJoinChoices} />
      </div>
    );
  }

  if (inWaitingRoom && !isApproved) {
    return (
      <div className="mursal-meeting-page">
        <WaitingRoom
          meetingId={meetingId}
          meetingName={meetingName}
          onApproved={handleApproved}
        />
      </div>
    );
  }

  if (!isApproved && !inWaitingRoom) {
    return (
      <div className="mursal-meeting-page">
        <div className="mursal-prejoin">
          <div className="mursal-prejoin__card">
            <h2 className="mursal-prejoin__title">{meetingName}</h2>
            <p className="mursal-prejoin__subtitle">Checking access...</p>
            <div
              className="mursal-loading__spinner"
              style={{ margin: "0 auto" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mursal-meeting-page">
      <LiveKitRoom
        serverUrl={connection.url}
        token={connection.token}
        connect={true}
        video={joinChoices.videoEnabled}
        audio={joinChoices.audioEnabled}
        onDisconnected={handleLeave}
        style={{ height: "100%" }}
      >
        <RoomAudioRenderer />
        <MeetingRoom
          meetingName={meetingName}
          meetingId={meetingId}
          hostId={hostId}
          onLeave={handleLeave}
        />
      </LiveKitRoom>
    </div>
  );
};

// ==================== INNER MEETING ROOM ====================
const MeetingRoom = ({ meetingName, meetingId, hostId, onLeave }) => {
  const room = useRoomContext();
  const allParticipants = useParticipants();
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } =
    useLocalParticipant();
  const screenShareTracks = useTracks([Track.Source.ScreenShare]);
  const activeScreenShare = screenShareTracks.find((t) => t.publication);
  const isLocalScreenSharing = screenShareTracks.some(
    (t) => t.participant?.identity === localParticipant?.identity,
  );
  const remoteParticipants = allParticipants.filter(
    (p) => p.identity !== localParticipant?.identity,
  );
  const isCurrentUserHost = localParticipant?.identity === hostId;

  const [isMuted, setIsMuted] = useState(!isMicrophoneEnabled);
  const [isVideoOff, setIsVideoOff] = useState(!isCameraEnabled);
  const [isScreenSharing, setIsScreenSharing] = useState(isLocalScreenSharing);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [remoteParticipantStates, setRemoteParticipantStates] = useState({});
  const timerRef = useRef(null);

  useEffect(() => {
    if (!localParticipant) return;
    const handleMetadataChanged = (metadata) => {
      try {
        const data = JSON.parse(metadata || "{}");
        if (data.videoDisabled === true)
          localParticipant.setCameraEnabled(false);
        else if (data.videoDisabled === false)
          localParticipant.setCameraEnabled(true);
      } catch (e) {}
    };
    if (localParticipant.metadata)
      handleMetadataChanged(localParticipant.metadata);
    localParticipant.on("metadataChanged", handleMetadataChanged);
    return () => {
      localParticipant.off("metadataChanged", handleMetadataChanged);
    };
  }, [localParticipant]);

  useEffect(() => {
    if (!room) return;
    const handleTrackMuted = (pub, participant) => {
      setRemoteParticipantStates((prev) => ({
        ...prev,
        [participant.identity]: {
          ...prev[participant.identity],
          isMuted:
            pub.kind === "audio" ? true : prev[participant.identity]?.isMuted,
          isCameraOff:
            pub.kind === "video"
              ? true
              : prev[participant.identity]?.isCameraOff,
        },
      }));
    };
    const handleTrackUnmuted = (pub, participant) => {
      setRemoteParticipantStates((prev) => ({
        ...prev,
        [participant.identity]: {
          ...prev[participant.identity],
          isMuted:
            pub.kind === "audio" ? false : prev[participant.identity]?.isMuted,
          isCameraOff:
            pub.kind === "video"
              ? false
              : prev[participant.identity]?.isCameraOff,
        },
      }));
    };
    room.on("trackMuted", handleTrackMuted);
    room.on("trackUnmuted", handleTrackUnmuted);
    return () => {
      room.off("trackMuted", handleTrackMuted);
      room.off("trackUnmuted", handleTrackUnmuted);
    };
  }, [room]);

  useEffect(() => {
    timerRef.current = setInterval(
      () => setElapsedTime((prev) => prev + 1),
      1000,
    );
    return () => clearInterval(timerRef.current);
  }, []);
  useEffect(() => {
    setIsMuted(!isMicrophoneEnabled);
    setIsVideoOff(!isCameraEnabled);
  }, [isMicrophoneEnabled, isCameraEnabled]);
  useEffect(() => {
    setIsScreenSharing(isLocalScreenSharing);
  }, [isLocalScreenSharing]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleToggleMic = async () => {
    try {
      await localParticipant.setMicrophoneEnabled(isMuted);
    } catch (e) {
      console.error(e);
    }
  };
  const handleToggleCamera = async () => {
    try {
      await localParticipant.setCameraEnabled(isVideoOff);
    } catch (e) {
      console.error(e);
    }
  };
  const handleToggleScreenShare = async () => {
    try {
      await localParticipant.setScreenShareEnabled(!isScreenSharing);
    } catch (e) {
      console.error(e);
    }
  };

  const handleStartRecording = async () => {
    try {
      const res = await startMeetingRecordingApi(meetingId);
      if (res.data?.success) {
        setIsRecording(true);
        setRecordingId(res.data?.data?.id || res.data?.recordingId);
      }
    } catch (e) {
      console.error(e);
    }
  };
  const handleStopRecording = async () => {
    try {
      if (recordingId) await stopRecordingApi(recordingId);
      setIsRecording(false);
      setRecordingId(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleMuteRemoteParticipant = async (participantId) => {
    try {
      const currentState = remoteParticipantStates[participantId] || {};
      const isCurrentlyMuted = currentState.isMuted;
      setRemoteParticipantStates((prev) => ({
        ...prev,
        [participantId]: { ...prev[participantId], isMuted: !isCurrentlyMuted },
      }));
      if (isCurrentlyMuted)
        await unmuteParticipantApi(meetingId, participantId);
      else await muteParticipantApi(meetingId, participantId);
    } catch (e) {
      console.error("Failed to toggle mute:", e);
    }
  };

  const handleDisableRemoteVideo = async (participantId) => {
    try {
      const currentState = remoteParticipantStates[participantId] || {};
      const isCameraCurrentlyOff = currentState.isCameraOff;
      setRemoteParticipantStates((prev) => ({
        ...prev,
        [participantId]: {
          ...prev[participantId],
          isCameraOff: !isCameraCurrentlyOff,
        },
      }));
      if (isCameraCurrentlyOff)
        await enableParticipantVideoApi(meetingId, participantId);
      else await disableParticipantVideoApi(meetingId, participantId);
    } catch (e) {
      console.error("Failed to toggle video:", e);
    }
  };

  const handleKickParticipant = async (participantId) => {
    try {
      if (!window.confirm("Remove this participant?")) return;
      await kickParticipantApi(meetingId, participantId);
    } catch (e) {
      console.error(e);
    }
  };

  const totalParticipants = remoteParticipants.length + 1;
  const getGridCols = () => {
    const c = totalParticipants;
    if (c <= 1) return "mursal-meeting__grid--cols-1";
    if (c <= 2) return "mursal-meeting__grid--cols-2";
    if (c <= 4) return "mursal-meeting__grid--cols-3";
    return "mursal-meeting__grid--cols-4";
  };
  const isParticipantHost = (p) => p?.identity === hostId;

  return (
    <div className="mursal-meeting">
      <header className="mursal-meeting__header">
        <div className="mursal-meeting__header-left">
          <div className="mursal-meeting__header-title-group">
            <h5>{meetingName}</h5>
            <div className="mursal-meeting__subtitle">
              <span>{totalParticipants} participants</span>
              {isRecording && (
                <span className="mursal-meeting__recording-badge">
                  <span className="mursal-meeting__recording-dot" />
                  Recording
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mursal-meeting__header-right">
          {isCurrentUserHost && (
            <button
              className="mursal-meeting__control-btn"
              onClick={() => setShowWaitingRoom(!showWaitingRoom)}
              data-tooltip="Waiting Room"
            >
              <WaitingRoomIcon />
            </button>
          )}
          <div className="mursal-meeting__live-badge">
            <span className="mursal-meeting__live-dot" />
            LIVE
          </div>
          <span className="mursal-meeting__timer">
            {formatTime(elapsedTime)}
          </span>
          <span className="mursal-meeting__id-chip">
            ID: {meetingId?.substring(0, 8)}
          </span>
        </div>
      </header>

      <div className="mursal-meeting__content">
        {activeScreenShare || isScreenSharing ? (
          <div className="mursal-meeting__screen-share-layout">
            <div className="mursal-meeting__screen-share-main">
              <ScreenShareView />
            </div>
            <div className="mursal-meeting__screen-share-sidebar">
              <div className="mursal-meeting__participant-tile">
                <ParticipantTile
                  participant={localParticipant}
                  isLocal={true}
                  isSpeaking={localParticipant?.isSpeaking}
                  isHost={isCurrentUserHost}
                />
              </div>
              {remoteParticipants.map((p) => (
                <div
                  key={p.identity}
                  className="mursal-meeting__participant-tile"
                >
                  <ParticipantTile
                    participant={p}
                    isSpeaking={p.isSpeaking}
                    isHost={isParticipantHost(p)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={`mursal-meeting__grid ${getGridCols()}`}>
            <ParticipantTile
              participant={localParticipant}
              isLocal={true}
              isSpeaking={localParticipant?.isSpeaking}
              isHost={isCurrentUserHost}
            />
            {remoteParticipants.map((p) => (
              <ParticipantTile
                key={p.identity}
                participant={p}
                isSpeaking={p.isSpeaking}
                isHost={isParticipantHost(p)}
              />
            ))}
          </div>
        )}
      </div>

      {showWaitingRoom && (
        <HostWaitingRoomPanel
          meetingId={meetingId}
          onClose={() => setShowWaitingRoom(false)}
        />
      )}

      {showParticipants && (
        <div className="mursal-meeting__participants-panel">
          <div className="mursal-meeting__participants-header">
            <h6>Participants ({totalParticipants})</h6>
            <button
              className="mursal-meeting__participants-close"
              onClick={() => setShowParticipants(false)}
            >
              ✕
            </button>
          </div>
          <div className="mursal-meeting__participants-list">
            <div className="mursal-participant-item">
              <div
                className="mursal-participant-item__avatar"
                style={{
                  backgroundColor: getAvatarColor(
                    localParticipant?.identity || "",
                  ),
                }}
              >
                {getInitials(localParticipant?.name || "You")}
                <span className="mursal-participant-item__status-dot" />
              </div>
              <div className="mursal-participant-item__info">
                <div className="mursal-participant-item__info-top">
                  <span className="mursal-participant-item__info-name">
                    {localParticipant?.name || "You"}
                  </span>
                  {isCurrentUserHost && (
                    <span className="mursal-participant-item__info-host-badge">
                      Host
                    </span>
                  )}
                </div>
                <div className="mursal-participant-item__info-email">
                  {localParticipant?.identity}
                </div>
              </div>
              <div className="mursal-participant-item__status">
                {!isMuted && <MicIcon />}
                {isVideoOff && <CameraOffIcon />}
              </div>
            </div>
            {remoteParticipants.map((p) => {
              const isHost = isParticipantHost(p);
              const localState = remoteParticipantStates[p.identity] || {};
              const isMuted =
                localState.isMuted !== undefined
                  ? localState.isMuted
                  : !p.isMicrophoneEnabled;
              const isCameraOff =
                localState.isCameraOff !== undefined
                  ? localState.isCameraOff
                  : !p.isCameraEnabled;
              const canControl = isCurrentUserHost && !isHost;
              return (
                <div key={p.identity} className="mursal-participant-item">
                  <div
                    className="mursal-participant-item__avatar"
                    style={{ backgroundColor: getAvatarColor(p.identity) }}
                  >
                    {getInitials(p.name || "Anonymous")}
                    <span className="mursal-participant-item__status-dot" />
                  </div>
                  <div className="mursal-participant-item__info">
                    <div className="mursal-participant-item__info-top">
                      <span className="mursal-participant-item__info-name">
                        {p.name || "Anonymous"}
                      </span>
                      {isHost && (
                        <span className="mursal-participant-item__info-host-badge">
                          Host
                        </span>
                      )}
                    </div>
                    <div className="mursal-participant-item__info-email">
                      {p.identity}
                    </div>
                  </div>
                  <div className="mursal-participant-item__status">
                    {!isMuted && <MicIcon />}
                    {isCameraOff && <CameraOffIcon />}
                  </div>
                  {canControl && (
                    <div className="mursal-participant-item__actions">
                      <button
                        className={`mursal-participant-item__action-btn ${isMuted ? "mursal-participant-item__action-btn--danger" : ""}`}
                        onClick={() => handleMuteRemoteParticipant(p.identity)}
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                      </button>
                      <button
                        className={`mursal-participant-item__action-btn ${isCameraOff ? "" : "mursal-participant-item__action-btn--active"}`}
                        onClick={() => handleDisableRemoteVideo(p.identity)}
                        title={isCameraOff ? "Enable camera" : "Disable camera"}
                      >
                        {isCameraOff ? <CameraOffIcon /> : <CameraIcon />}
                      </button>
                      <button
                        className="mursal-participant-item__action-btn mursal-participant-item__action-btn--danger"
                        onClick={() => handleKickParticipant(p.identity)}
                        title="Remove participant"
                      >
                        <KickIcon />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {isCurrentUserHost && remoteParticipants.length > 0 && (
            <div className="mursal-meeting__participants-host-actions">
              <span className="mursal-meeting__participants-host-actions__label">
                Host Controls
              </span>
              <div className="mursal-meeting__participants-host-actions__actions">
                <button
                  className="mursal-meeting__participants-host-actions__btn"
                  onClick={() => {
                    remoteParticipants.forEach((p) => {
                      const s = remoteParticipantStates[p.identity] || {};
                      if (!s.isMuted) handleMuteRemoteParticipant(p.identity);
                    });
                  }}
                >
                  <MicOffIcon /> Mute All
                </button>
                <button
                  className="mursal-meeting__participants-host-actions__btn"
                  onClick={() => {
                    remoteParticipants.forEach((p) => {
                      const s = remoteParticipantStates[p.identity] || {};
                      if (s.isMuted) handleMuteRemoteParticipant(p.identity);
                    });
                  }}
                >
                  <MicIcon /> Unmute All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mursal-meeting__controls">
        <div className="mursal-meeting__controls-inner">
          <div className="mursal-meeting__controls-group">
            <button
              className={`mursal-meeting__control-btn ${isMuted ? "" : "mursal-meeting__control-btn--active"}`}
              onClick={handleToggleMic}
              data-tooltip={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </button>
            <button
              className={`mursal-meeting__control-btn ${isVideoOff ? "" : "mursal-meeting__control-btn--active"}`}
              onClick={handleToggleCamera}
              data-tooltip={isVideoOff ? "Turn on camera" : "Turn off camera"}
            >
              {isVideoOff ? <CameraOffIcon /> : <CameraIcon />}
            </button>
          </div>
          <div className="mursal-meeting__controls-divider" />
          <div className="mursal-meeting__controls-group">
            <button
              className={`mursal-meeting__control-btn ${isScreenSharing ? "mursal-meeting__control-btn--active" : ""}`}
              onClick={handleToggleScreenShare}
              data-tooltip="Share screen"
            >
              <ScreenShareIcon />
            </button>
            <button
              className={`mursal-meeting__control-btn ${isRecording ? "mursal-meeting__control-btn--recording" : ""}`}
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              data-tooltip={isRecording ? "Stop recording" : "Start recording"}
            >
              {isRecording ? <StopIcon /> : <RecordIcon />}
            </button>
          </div>
          <div className="mursal-meeting__controls-divider" />
          <button
            className="mursal-meeting__control-btn"
            data-tooltip="Participants"
            onClick={() => setShowParticipants(!showParticipants)}
          >
            <ParticipantsIcon />
          </button>
          <div className="mursal-meeting__controls-divider" />
          <button
            className="mursal-meeting__leave-btn"
            onClick={onLeave}
            data-tooltip="Leave meeting"
          >
            <LeaveIcon />
            <span>Leave</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RealMeeting;
